// @givmo/app-admin — Employer BULK gift-card decode/build helpers (EP-WEB5). ★ MONEY-MOVING ★
//
// Pure, side-effect-free helpers for the employer BULK-gifting flow — a company sending N
// company-funded gift cards of a UNIFORM denomination to roster employees via
// `POST /admin/company/gift-cards/batches` (EP-BE3, #1327). Kept as their own no-React module so the
// paste parser, the `CompanyGiftCardBatchSend` body construction, the batches-list decode, the
// per-entry 422 extraction, and the batch-status/delivery-count display are all unit-testable in
// isolation (mirroring the single-send `gift-cards-decode.ts` split it builds on).
//
// Numbers-are-the-law: the uniform `denomination` crosses the wire as a decimal STRING; the face
// TOTAL shown in the confirm is a plain count × whole-dollar-denomination (exact integer math, never a
// re-round of a wire money value). The company-CARD path is charged the GROSS (face + a processor
// gross-up the company always covers, D-GC-2); that gross is NOT on the batch response and MUST NOT be
// recomputed client-side (numbers-are-the-law) — so the confirm states face + "card processing added
// at checkout" rather than a fabricated total. The company-WALLET path debits EXACTLY face.
//
// Tolerant reader: the batches-list decode reads the `{ data: [...] }` envelope and degrades to `[]`;
// the per-entry 422 extractor degrades to `[]`; the status/funding labels never throw on an unknown.

import {
  type CompanyGiftCardBatchOut,
  type CompanyGiftCardBatchSend,
  type CompanyGiftCardBatchRecipient,
  type GiftCardBatchStatus,
  type GivmoError,
  firstErrorCode,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { parseCentsAmount, formatCentsUsd } from '../dashboard/dashboard-decode';
import type { StatedGiftCardCharge } from './gift-card-quote';
import {
  fundingSource,
  formatGiftCardDollars,
  CARD_PAYMENT_METHOD,
  type Funding,
} from './gift-cards-decode';

/**
 * The batch send's body: the generated `CompanyGiftCardBatchSend`, plus the stated price a CARD batch
 * carries for its one charge (GC-FU31) — declared ahead of a snapshot regen, as the single send's is.
 */
export type CompanyGiftCardBatchSendBody = CompanyGiftCardBatchSend & Partial<StatedGiftCardCharge>;

// ── Batch-count cap (D-EP-16) ────────────────────────────────────────────────────────────────────

/**
 * The per-batch recipient COUNT cap surfaced client-side. Mirrors the backend default
 * `EMPLOYER_GIFT_CARD_BATCH_MAX_COUNT` (500) so an over-cap batch is caught BEFORE the money POST
 * rather than only by the server 400. The server remains authoritative (an operator may tune its
 * knob); this is a UX pre-check that fails conservatively — a batch at/under it may still be rejected
 * server-side, and the server's message is surfaced verbatim if so.
 */
export const GIFT_CARD_BATCH_MAX_COUNT = 500;

// ── Recipient (roster employee) ───────────────────────────────────────────────────────────────────

/** One resolved batch recipient: the work email (delivery address + `work_email` body field) plus the
 *  optional display name. Selected via the roster multi-select OR parsed from the CSV/text paste. */
export interface BatchRecipient {
  /** The work email — the delivery address + the recipient `work_email` body field. */
  readonly workEmail: string;
  /** Optional display name (present for roster-picked recipients; absent for pasted emails). */
  readonly name?: string | null;
}

/** Case-insensitive key for deduping a recipient by work email (one person ⇒ one card — the backend
 *  mints one card PER entry and does NOT dedupe, so a duplicate here would double-gift). */
export function recipientDedupeKey(workEmail: string): string {
  return workEmail.trim().toLowerCase();
}

/**
 * The batch's ONE canonical recipient order (GC-FU13): by {@link recipientDedupeKey}. The idempotency
 * fingerprint (`giftCardBatchFingerprint`) and the send body (`buildCompanyGiftCardBatchBody`) both
 * take it, so a retry of the same recipient SET under the same key sends the same bytes and the
 * backend replays the original batch. With the body in entry order, a retry whose recipients had been
 * reordered kept the key but changed the bytes, and drew `IDEMPOTENCY_KEY_REUSED` on every attempt.
 */
export function inCanonicalRecipientOrder<T>(
  items: readonly T[],
  workEmail: (item: T) => string,
): T[] {
  return [...items].sort((a, b) => {
    const left = recipientDedupeKey(workEmail(a));
    const right = recipientDedupeKey(workEmail(b));
    return left < right ? -1 : left > right ? 1 : 0;
  });
}

// ── CSV / text paste parsing ──────────────────────────────────────────────────────────────────────

/** Shape check for a pasted token. Deliberately lenient — the roster (server) is the truth on whether
 *  an address is giftable; this only rejects obvious non-emails so they surface as `invalid` locally
 *  instead of 422-ing the whole batch. */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** The result of parsing a paste blob: the deduped valid work emails (original case preserved — the
 *  backend matches `work_email` EXACTLY, so we never lowercase what we send), the non-email tokens, and
 *  how many duplicate valid tokens were collapsed (for a "removed N duplicates" hint). */
export interface ParsedRecipientPaste {
  readonly emails: string[];
  readonly invalid: string[];
  readonly duplicates: number;
}

/**
 * Parse a CSV/text paste of work emails: one-per-line OR comma/semicolon/space-separated. Trims each
 * token, drops empties, classifies each as a shaped email (→ `emails`) or junk (→ `invalid`), and
 * dedupes the valid set case-insensitively while PRESERVING the first token's original case (the
 * backend's exact match means we must not mangle case). Never throws.
 */
export function parseRecipientPaste(raw: string | null | undefined): ParsedRecipientPaste {
  const emails: string[] = [];
  const invalid: string[] = [];
  const seen = new Set<string>();
  let duplicates = 0;

  if (typeof raw !== 'string' || raw.trim().length === 0) {
    return { emails, invalid, duplicates };
  }

  for (const token of raw.split(/[\s,;]+/)) {
    const trimmed = token.trim();
    if (trimmed.length === 0) continue;
    if (!EMAIL_SHAPE.test(trimmed)) {
      invalid.push(trimmed);
      continue;
    }
    const key = recipientDedupeKey(trimmed);
    if (seen.has(key)) {
      duplicates += 1;
      continue;
    }
    seen.add(key);
    emails.push(trimmed);
  }

  return { emails, invalid, duplicates };
}

/**
 * Merge an incoming set of work emails into an existing recipient list, deduping case-insensitively and
 * KEEPING the existing entries (so a roster-picked recipient's name survives a later paste of the same
 * address). Returns the merged list + the count of skipped duplicates. Order: existing first, then the
 * genuinely-new incoming ones in arrival order.
 */
export function mergeRecipients(
  existing: readonly BatchRecipient[],
  incoming: readonly BatchRecipient[],
): { recipients: BatchRecipient[]; added: number; skipped: number } {
  const seen = new Set(existing.map((r) => recipientDedupeKey(r.workEmail)));
  const recipients = [...existing];
  let added = 0;
  let skipped = 0;
  for (const r of incoming) {
    const key = recipientDedupeKey(r.workEmail);
    if (seen.has(key)) {
      skipped += 1;
      continue;
    }
    seen.add(key);
    recipients.push(r);
    added += 1;
  }
  return { recipients, added, skipped };
}

// ── Face total ────────────────────────────────────────────────────────────────────────────────────

/** The whole-dollar face TOTAL for a batch: uniform denomination × count. Exact integer math on the
 *  count and the whole-dollar denomination — NOT a re-round of a wire money value. */
export function batchFaceTotalDollars(denomination: number, count: number): number {
  return denomination * count;
}

/** Formatted face total ("$500") for the itemization/list. */
export function formatBatchFaceTotal(denomination: number, count: number): string {
  return formatGiftCardDollars(batchFaceTotalDollars(denomination, count));
}

/** Formatted face total for the batches REPORT, where the per-card `denomination` arrives as a decimal
 *  wire STRING ("50" / "50.00"). Multiplies EXACT integer cents by the count via string math (never a
 *  float / re-round); "—" when the denomination is blank/unparseable. */
export function formatBatchTotalFromWire(
  denominationWire: string | null | undefined,
  quantity: number,
): string {
  const cents = parseCentsAmount(denominationWire ?? '');
  if (cents === null || !Number.isInteger(quantity) || quantity < 0) return '—';
  return formatCentsUsd(cents * quantity);
}

// ── Send body ─────────────────────────────────────────────────────────────────────────────────────

function presentOrUndefined(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed && trimmed.length > 0 ? trimmed : undefined;
}

/**
 * Build the `CompanyGiftCardBatchSend` body. `denomination` is the whole-dollar integer (validated in
 * range by the caller) sent as a decimal STRING. A CARD batch carries `funding_source:"company_card"`
 * + `card_id` + `payment_method:"Credit Card"`; a WALLET batch carries `funding_source:"company_wallet"`
 * only. Each recipient is `{ work_email, name? }` (name omitted when blank). The SAME stable
 * `purchase_request_id` is the batch idempotency key (also sent in the `Idempotency-Key` header), so a
 * retried send de-dupes to the ORIGINAL batch and never double-charges. The recipients go in the
 * canonical order the key's fingerprint uses ({@link inCanonicalRecipientOrder}), so the same set sends
 * the same bytes whatever order it was entered in. Batch name/message omitted when blank.
 */
export function buildCompanyGiftCardBatchBody(params: {
  denomination: number;
  funding: Funding;
  recipients: readonly BatchRecipient[];
  batchName?: string | null;
  recipientMessage?: string | null;
  purchaseRequestId: string;
  /** A CARD batch's stated price for its ONE charge (GC-FU31); `null`/absent on the wallet. */
  stated?: StatedGiftCardCharge | null;
}): CompanyGiftCardBatchSendBody {
  const ordered = inCanonicalRecipientOrder(params.recipients, (r) => r.workEmail);
  const recipients: CompanyGiftCardBatchRecipient[] = ordered.map((r) => {
    const entry: CompanyGiftCardBatchRecipient = { work_email: r.workEmail };
    const name = presentOrUndefined(r.name);
    if (name !== undefined) entry.name = name;
    return entry;
  });

  const body: CompanyGiftCardBatchSendBody = {
    denomination: String(params.denomination),
    funding_source: fundingSource(params.funding),
    recipients,
    purchase_request_id: params.purchaseRequestId,
  };

  if (params.funding.kind === 'card') {
    body.card_id = params.funding.cardId;
    body.payment_method = CARD_PAYMENT_METHOD;
    if (params.stated) Object.assign(body, params.stated);
  }

  const name = presentOrUndefined(params.batchName);
  const message = presentOrUndefined(params.recipientMessage);
  if (name !== undefined) body.name = name;
  if (message !== undefined) body.recipient_message = message;

  return body;
}

// ── Batches-list decode ───────────────────────────────────────────────────────────────────────────

/** Tolerant `{ data: [...] }` decode → the company's gift-card batches. Never throws (→ `[]`). */
export function extractCompanyGiftCardBatches(body: unknown): CompanyGiftCardBatchOut[] {
  return extractList<CompanyGiftCardBatchOut>(body);
}

/** Human label for the batch lifecycle status (`GiftCardBatchStatus`). Tolerant of an unknown value
 *  (→ "—"), never throws. v1 batches are CREATED; a super-admin void moves it to VOIDED. */
export function batchStatusLabel(status: GiftCardBatchStatus | null | undefined): string {
  switch (status) {
    case 'CREATED':
      return 'Created';
    case 'EXPORTED':
      return 'Exported';
    case 'ACTIVATED':
      return 'Activated';
    case 'VOIDED':
      return 'Voided';
    default:
      return '—';
  }
}

// ── Enqueue-failure surfacing (D-EP-16(d)) ─────────────────────────────────────────────────────────

/**
 * The count of cards in a batch whose async delivery could NOT be enqueued (their one-time code is lost
 * — terminally undeliverable, recoverable only by a super-admin void+reissue). Read from the
 * create-201 `delivery_enqueue_failed`; 0/absent means every card was queued. NEVER silent — the UI
 * raises a prominent warning whenever this is > 0.
 */
export function batchEnqueueFailedCount(batch: CompanyGiftCardBatchOut): number {
  const n = batch.delivery_enqueue_failed;
  return typeof n === 'number' && n > 0 ? n : 0;
}

// ── Per-entry 422 (BATCH_RECIPIENTS_INVALID) extraction ────────────────────────────────────────────

/** One offending recipient from the server's all-or-nothing 422 — the row `index`, the `work_email`
 *  it saw, and the machine `reason` (`not_on_roster` / `blank_work_email`), each tolerated-optional. */
export interface BatchRecipientError {
  readonly index: number | null;
  readonly workEmail: string | null;
  readonly reason: string | null;
}

/** The stable machine `error_code` the backend attaches to the all-or-nothing roster-rejection 422. */
export const BATCH_RECIPIENTS_INVALID_CODE = 'BATCH_RECIPIENTS_INVALID';

/** Whether a failed batch send is the roster-rejection 422 (the WHOLE batch was rejected pre-charge —
 *  a DEFINITE no-charge — with a per-entry `invalid_recipients` list). */
export function isBatchRecipientsInvalidError(error: GivmoError): boolean {
  return firstErrorCode(error) === BATCH_RECIPIENTS_INVALID_CODE;
}

/**
 * Extract the per-entry offending rows from a `BATCH_RECIPIENTS_INVALID` 422's
 * `context.invalid_recipients` (`[{index, work_email, reason}]`). Tolerant: reads the additive
 * envelope `context` bag, degrades to `[]` on any unexpected shape, and coerces each entry's fields
 * defensively (a malformed row still yields a renderable `{index:null, workEmail:null, reason:null}`).
 * Never throws — the confirm always has SOMETHING to render for the bad rows.
 */
export function extractInvalidBatchRecipients(error: GivmoError): BatchRecipientError[] {
  const raw = error.entries[0]?.context?.invalid_recipients;
  if (!Array.isArray(raw)) return [];
  const out: BatchRecipientError[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const r = item as Record<string, unknown>;
    out.push({
      index: typeof r.index === 'number' ? r.index : null,
      workEmail: typeof r.work_email === 'string' ? r.work_email : null,
      reason: typeof r.reason === 'string' ? r.reason : null,
    });
  }
  return out;
}

/** Human copy for a per-entry rejection reason (tolerant of an unknown reason → a generic line). */
export function batchRecipientReasonLabel(reason: string | null): string {
  switch (reason) {
    case 'not_on_roster':
      return 'Not on your company roster';
    case 'blank_work_email':
      return 'Missing a work email';
    default:
      return 'Cannot receive a gift card';
  }
}
